// The one fetch wrapper (ADR 0006). Reproduces the portal's useHttpClient +
// useFetch: base URL, auth header, JSON parsing and error-message extraction.
// It never shows a toast: callers (react-query's MutationCache, screens)
// decide how an error is surfaced.
import { apiBaseUrl } from './overrides'
import { HttpError, type HttpAuth } from './types'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface RequestOptions {
  /** A ready query string (no leading `?`), URLSearchParams, or flat string pairs. */
  query?: URLSearchParams | Record<string, string> | string
  /** Serialised as JSON unless it is FormData or already a string. */
  body?: unknown
  /** Sent as `X-TenantId` (only where the portal sends it — PARITY Q1). */
  tenantId?: string | number
  signal?: AbortSignal
  headers?: Record<string, string>
  /** `path` is a full URL (project API, folders APIs); the base URL is not prepended. */
  absolute?: boolean
}

export interface HttpResponse<T> {
  json: T
  headers: Headers
  status: number
}

const signedOut: HttpAuth = {
  getToken: () => Promise.resolve(null),
  onUnauthorized: () => undefined
}

let auth: HttpAuth = signedOut

/** Wire the auth layer in (app/providers). Until then requests go out without a token. */
export function configureHttp(next: HttpAuth): void {
  auth = next
}

function buildUrl(path: string, options: RequestOptions): string {
  const base = options.absolute ? path : `${apiBaseUrl()}/${path.replace(/^\//, '')}`
  const { query } = options
  if (query === undefined) return base
  const qs =
    typeof query === 'string'
      ? query
      : (query instanceof URLSearchParams ? query : new URLSearchParams(query)).toString()
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base
}

/**
 * The portal's error message precedence (portal: src/hooks/useFetch.ts:78-96):
 * error → errorDetails → ErrorDetails → detail → message → a string body →
 * JSON.stringify(body) → statusText → 'Request error'.
 */
export function errorMessage(json: unknown, statusText: string): string {
  const obj =
    json !== null && typeof json === 'object' ? (json as Record<string, unknown>) : undefined
  const picked: unknown =
    obj?.error ||
    obj?.errorDetails ||
    obj?.ErrorDetails ||
    obj?.detail ||
    obj?.message ||
    (typeof json === 'string' ? json : undefined) ||
    (obj ? JSON.stringify(obj) : undefined) ||
    statusText ||
    'Request error'
  return String(picked)
}

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData
}

/**
 * One API request. Returns the parsed body (`undefined` for an empty body, the
 * raw text when it is not JSON), the response headers and the status.
 * Non-2xx throws HttpError; 401 also signs the user out first (ADR 0008).
 */
export async function request<T = unknown>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {}
): Promise<HttpResponse<T>> {
  const url = buildUrl(path, options)

  // Deviation (recorded in PARITY Data/getOne): the portal replaces its default
  // headers with `new Headers({'X-TenantId'})`, dropping `Accept`. We always send it.
  const headers = new Headers({ Accept: 'application/json' })
  const { body } = options
  const hasBody = body !== undefined && body !== null
  if (hasBody && method !== 'GET' && !isFormData(body)) {
    headers.set('Content-Type', 'application/json')
  }
  if (options.tenantId !== undefined && options.tenantId !== '') {
    headers.set('X-TenantId', String(options.tenantId))
  }

  let token: string | null
  try {
    token = await auth.getToken()
  } catch (error) {
    // The portal's "Invalid OASE access token" → forceLogout path.
    auth.onUnauthorized()
    throw new HttpError(401, error instanceof Error ? error.message : 'Unauthorized', undefined)
  }
  if (token) headers.set('Authorization', `Bearer ${token}`)

  for (const [name, value] of Object.entries(options.headers ?? {})) headers.set(name, value)

  const init: RequestInit = { method, headers }
  if (hasBody) {
    init.body = isFormData(body) || typeof body === 'string' ? body : JSON.stringify(body)
  }
  if (options.signal) init.signal = options.signal

  const response = await fetch(url, init)
  const text = await response.text()
  let json: unknown = undefined
  if (text) {
    try {
      json = JSON.parse(text)
    } catch {
      json = text
    }
  }

  if (response.status < 200 || response.status >= 300) {
    const error = new HttpError(response.status, errorMessage(json, response.statusText), json)
    if (response.status === 401) auth.onUnauthorized()
    // 403 is thrown as is: the screen shows access denied (ADR 0008).
    throw error
  }

  return { json: json as T, headers: response.headers, status: response.status }
}
